/**
 * @file apps/web/src/lib/supabase.ts
 * @description Supabase client for the frontend — real authentication only
 * (supabase.auth.signInWithPassword, and the onAuthStateChange subscriptions
 * in lib/auth.tsx / lib/portalAuth.tsx that keep the access token fresh).
 * VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are Vite build-time
 * vars, inlined into the static bundle (see apps/web/Dockerfile) — there is
 * no runtime server to read env vars from after the nginx stage, same as
 * VITE_API_URL in lib/api.ts.
 *
 * The dev quick-login path (LoginPage.tsx / PortalLoginPage.tsx, DEV_MODE
 * only) never touches this client — it calls POST /auth/dev-login and
 * /auth/dev-token on the API directly. So a misconfigured/missing Supabase
 * project locally does not block dev quick-login; it only breaks the
 * "real credentials" form submit, which fails inside signInWithPassword()
 * and surfaces as a normal login error rather than crashing the app.
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl            = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const supabasePublishableKey = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'] as string | undefined;

if (import.meta.env.PROD && (!supabaseUrl || !supabasePublishableKey)) {
  // Don't throw — a thrown error here would white-screen the entire app at
  // module load, including the SI/portal login pages themselves. Log loudly
  // instead; signInWithPassword() will fail with a clear network/auth error
  // that the login pages already catch and display.
  // eslint-disable-next-line no-console
  console.error(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY are not set in this build — ' +
    'real sign-in (supabase.auth.signInWithPassword) will fail until they are configured.',
  );
}

export const supabase = createClient(
  supabaseUrl ?? 'http://localhost:54321',
  supabasePublishableKey ?? 'sb_publishable_unconfigured',
);
